import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { PlatformAccess, PLATFORM_OPTIONS } from "@/components/PlatformAccess";

const EMPTY = { name: "", email: "", password: "", phone: "", platforms: [], recipients: "", rates: {} };

export const SubAgentFormDialog = ({ open, sub, myPlatforms, onClose }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const opts = PLATFORM_OPTIONS.filter(([k]) => myPlatforms.includes(k));
  const wasOpen = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!justOpened) return;
    setF(sub ? { name: sub.name, email: sub.email, password: "", phone: sub.phone || "", platforms: sub.allowed_platforms,
      recipients: (sub.allowed_recipients || []).join(", "), rates: sub.sub_rates || {} } : { ...EMPTY, platforms: myPlatforms });
  }, [open, sub, myPlatforms]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    if (!f.platforms.length) { toast.error("Pick at least one platform"); return; }
    const body = {
      name: f.name, phone: f.phone, allowed_platforms: f.platforms,
      allowed_recipients: f.recipients.split(/[\s,]+/).filter(Boolean), sub_rates: f.rates,
      ...(f.password ? { password: f.password } : {}),
    };
    setBusy(true);
    try {
      if (sub) await api.patch(`/reseller/subagents/${sub.id}`, body);
      else await api.post("/reseller/subagents", { ...body, email: f.email });
      toast.success(sub ? "Sub-agent updated" : "Sub-agent created — share the email & password with them");
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="subagent-form-dialog">
        <DialogHeader><DialogTitle>{sub ? `Edit ${sub.name}` : "Add sub-agent"}</DialogTitle>
          <DialogDescription>They sign in at the reseller login with this email and password.</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={f.name} onChange={set("name")} data-testid="subagent-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">WhatsApp / phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="subagent-phone-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email (login)</Label><Input type="email" disabled={!!sub} value={f.email} onChange={set("email")} data-testid="subagent-email-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{sub ? "New password (optional)" : "Password"}</Label><Input type="text" value={f.password} onChange={set("password")} data-testid="subagent-password-input" /></div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Platforms they can recharge</Label>
          <PlatformAccess value={f.platforms} onChange={(v) => setF((s) => ({ ...s, platforms: v }))} options={opts} testid="subagent-platforms" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Only these in-app IDs (optional — leave empty to allow anyone)</Label>
          <Textarea rows={2} placeholder="e.g. 2097459914, 2096611852" value={f.recipients} onChange={set("recipients")} data-testid="subagent-recipients-input" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Your rate per diamond for this sub-agent (₹, for your records)</Label>
          <div className="flex flex-wrap gap-2">
            {opts.filter(([k]) => f.platforms.includes(k)).map(([k, n]) => (
              <div key={k} className="flex items-center gap-1.5 text-xs"><span className="text-zinc-500">{n}</span>
                <Input type="number" step="0.01" min="0" className="w-24 h-8" value={f.rates[k] ?? ""} onChange={(e) => setF((s) => ({ ...s, rates: { ...s.rates, [k]: e.target.value } }))} data-testid={`subagent-rate-${k}`} /></div>
            ))}
          </div></div>
        <Button onClick={save} disabled={busy} className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="subagent-save-button">{busy ? "Saving…" : sub ? "Save changes" : "Create sub-agent"}</Button>
      </DialogContent>
    </Dialog>
  );
};
